import { addIndividualObjectMutation } from './actions/addIndividualObjectMutation';
import { deleteQuery } from './actions/deleteQuery';
import { addObjectToQuery } from './actions/addObjectToQuery';
import { editQuery } from './actions/editQuery';
import { clearSyncedObjectMutations } from './actions/clearSyncedObjectMutations';
export const OBJECT_DELETED = 'OBJECT_DELETED';
export const QUERY_DELETED = 'QUERY_DELETED';
/**
 * This should not be used other than in tests, or to reset the store
 *
 * This is explicitly a getter, rather than a static object. If it were a static object, and if there were
 * multiple store instances, they would have the same references for individualObjects and queryMutations leading to
 * states being written to multiple stores at once
 *
 * By putting this in a getter, we esnure new objects are being made for each call
 */
export const getLocalMutationsStoreDefaultState = () => ({
  individualObjects: {},
  queryMutations: {}
});
export const createLocalMutationsStore = () => {
  const subscribers = new Set();
  let state = getLocalMutationsStoreDefaultState();
  return {
    notify: () => {
      subscribers.forEach(sub => sub());
    },
    getSnapshot() {
      return state;
    },
    subscribe(subscriber) {
      subscribers.add(subscriber);
      return () => {
        subscribers.delete(subscriber);
      };
    },
    addMultipleObjectMutations(input) {
      state = input.objectIds.reduce((stateObj, currentObjectId) => {
        return addIndividualObjectMutation({
          currentState: stateObj,
          input: {
            objectTypeId: input.objectTypeId,
            objectId: currentObjectId,
            mutation: input.mutation
          }
        });
      }, state);
      this.notify();
    },
    addIndividualObjectMutation(input) {
      state = addIndividualObjectMutation({
        currentState: state,
        input
      });
      this.notify();
    },
    deleteQuery({
      queryHash
    }) {
      state = deleteQuery({
        queryHash,
        currentState: state
      });
      this.notify();
    },
    addObjectToQuery({
      queryHash,
      objectId
    }) {
      state = addObjectToQuery({
        input: {
          queryHash,
          objectId
        },
        currentState: state
      });
      this.notify();
    },
    addQueryMutation({
      queryHash,
      mutation
    }) {
      if (mutation === QUERY_DELETED) {
        state = deleteQuery({
          queryHash,
          currentState: state
        });
      } else {
        state = editQuery({
          input: {
            queryHash,
            edits: mutation
          },
          currentState: state
        });
      }
      this.notify();
    },
    /**
     * ## Motivation
     * Helps sync local mutations with server updates from different UI interactions.
     *
     * On the CRM Index Page, inline edits are optimistically persisted as local
     * mutations, avoiding immediate query refetches for performance, while Preview
     * Panel edits trigger server-side updates and subsequent query refetches via
     * message bus events.
     *
     * ## Problem
     * If we don't clear local mutations, they could override newer server data after
     * Preview Panel edits, causing the UI to show outdated information.
     *
     * ## Solution
     * We clear synced object mutations when we get server updates. This lets server
     * data take priority after a refresh, while keeping the performance benefits of
     * local mutations for inline edits.
     *
     * ## Delete Mutation Handling
     * **Preserved**: We keep delete mutations rather than clearing them.
     * **Reason**:
     *    - Delete mutations need to stay to properly show what's been removed
     *    - Server search results may briefly include deleted items due to indexing lag
     *    - Keeping delete mutations locally helps hide deleted items consistently
     *    - Since we can't confirm server-side deletion status reliably, we keep
     *      these mutations to maintain consistency
     *
     * ## Scope
     * This clears individual object mutations for a specific object type, but leaves
     * query-level mutations unchanged as those are handled separately.
     *
     * @param objectTypeId - Object type ID to clear mutations for
     */
    clearSyncedObjectMutations(objectTypeId) {
      state = clearSyncedObjectMutations({
        currentState: state,
        objectTypeId
      });
      this.notify();
    }
  };
};